import { supabase } from '@/lib/supabase/client'
import type { ExportBundle } from '@/lib/api/admin-layouts'

export interface AdminLocation {
  id: string
  user_id: string
  name: string
  latitude: number
  longitude: number
  airport_code: string | null
  airport_name: string | null
  layout_count: number
}

/**
 * Direct PostgREST read used by the dashboard map (allowed by the
 * `admin_locations_select` RLS policy). Unpaginated on purpose — the map plots
 * every point. Browsing/detail/export go through the admin-locations function.
 */
export async function fetchAllLocations(): Promise<AdminLocation[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('id, user_id, name, latitude, longitude, airport_code, airport_name, layout_count')
    .is('deleted_at', null)
  if (error) throw error
  return data as AdminLocation[]
}

// ─── admin-locations edge function ──────────────────────────────────────────

export interface AdminLocationOwner {
  email: string
  display_name: string | null
  tier: string
}

export interface AdminLocationRow {
  id: string
  user_id: string
  name: string
  latitude: number
  longitude: number
  airport_code: string | null
  airport_name: string | null
  slug: string | null
  address: string | null
  thumbnail_url: string | null
  layout_count: number
  deleted_at: string | null
  created_at: string
  updated_at: string
  owner: AdminLocationOwner
}

export interface LocationListParams {
  page?: number
  pageSize?: number
  search?: string
  includeDeleted?: boolean
}

export interface LocationListResult {
  locations: AdminLocationRow[]
  total: number
  page: number
  pageSize: number
}

/** One layout of a location — metadata + derived counts, no scene_data. */
export interface AdminLocationLayout {
  id: string
  name: string
  user_id: string
  location_id: string
  thumbnail_url: string | null
  is_public: boolean
  share_token: string | null
  display_order: number
  revision: number
  published_revision: number | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  building_count: number
  /** Bays across multi-unit rows (single-volume hangars count 0). */
  unit_count: number
  item_count: number
  /** Airport structures (presets, jet bridges, control towers) — stored as items. */
  structure_count: number
}

export interface AdminLocationDetail {
  location: Record<string, unknown> & {
    id: string
    name: string
    user_id: string
    latitude: number
    longitude: number
    airport_code: string | null
    airport_name: string | null
    slug: string | null
    address: string | null
    thumbnail_url: string | null
    layout_count: number
    deleted_at: string | null
    created_at: string
    updated_at: string
  }
  owner: { id: string; email: string; display_name: string | null; tier: string }
  layouts: AdminLocationLayout[]
}

async function invokeAdminLocations<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-locations', { body })
  if (error) throw new Error(String(error))
  if (data?.error) throw new Error(data.error)
  return data as T
}

export function fetchAdminLocations(params: LocationListParams) {
  return invokeAdminLocations<LocationListResult>({ action: 'list', ...params })
}

export function fetchAdminLocationDetail(locationId: string) {
  return invokeAdminLocations<AdminLocationDetail>({ action: 'get', locationId })
}

/** Full JSON bundle for a location + all of its layouts. Audit-logged server-side. */
export function exportLocation(locationId: string, opts: { includeDeleted?: boolean } = {}) {
  return invokeAdminLocations<ExportBundle>({
    action: 'export',
    locationId,
    includeDeleted: opts.includeDeleted,
  })
}
